/**
 * Records the bank UTR that refunds an expired open-advance entry (ENG-27799).
 *
 * The refund is terminal — VIP has no un-refund — so the vendor must confirm
 * the transfer before the button enables. No amount is sent: VIP then refunds
 * the whole residual, the only shape it completes.
 *
 * Status handling follows VIP's contract: a field error for 400, the backend's
 * own message for 422 (it names the residual), and for 404 / 409 / 503 a toast
 * and the dialog closes — the entry changed or the flow is off, and the list
 * refetch (see useRecordRefundUtr) shows the truth.
 */
import { useEffect, useState } from "react";
import { Box, Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";

import BaseModal from "@/pages/InvoiceManagement/v2/components/BaseModal";
import { useToast } from "@/pages/InvoiceManagement/hooks/useToast";
import { getBackendErrorMessage } from "@/helpers/errorUtils";
import { useRecordRefundUtr } from "../api/walletMutations";
import type { WalletEntry } from "../types/wallet";
import { formatAmount } from "../utils/formatters";
import { entrySourceLabel } from "../utils/openAdvance";

interface RefundUtrDialogProps {
  /** The EXPIRED entry being refunded; null keeps the dialog closed. */
  entry: WalletEntry | null;
  onClose: () => void;
}

export default function RefundUtrDialog({ entry, onClose }: RefundUtrDialogProps) {
  const { showToast } = useToast();
  const mutation = useRecordRefundUtr();
  const [utr, setUtr] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [fieldError, setFieldError] = useState("");
  const [formError, setFormError] = useState("");

  // A fresh form for every entry the dialog opens on.
  useEffect(() => {
    setUtr("");
    setConfirmed(false);
    setFieldError("");
    setFormError("");
  }, [entry?.id]);

  if (!entry) return null;

  const amount = formatAmount(entry.balance);
  const trimmed = utr.trim();

  const handleConfirm = () => {
    setFieldError("");
    setFormError("");
    mutation.mutate(
      { entryId: entry.id, facilityId: entry.vendor_facility_id, utrNumber: trimmed },
      {
        onSuccess: () => {
          showToast({ type: "success", message: `Refund of ${amount} recorded.` });
          onClose();
        },
        onError: (error: any) => {
          const status = error?.response?.status;
          const message = getBackendErrorMessage(error, "Couldn't record the refund. Please try again.");
          if (status === 400) {
            setFieldError(message);
          } else if (status === 404) {
            showToast({ type: "error", message: "This credit was not found. The list has been refreshed." });
            onClose();
          } else if (status === 409) {
            showToast({ type: "error", message: "A refund was already recorded for this credit." });
            onClose();
          } else if (status === 503) {
            // The flow was switched off; the refetch hides the section. Say so,
            // or a vendor who just entered a UTR may think it was recorded.
            showToast({ type: "error", message: "Refunds are unavailable right now. Nothing was recorded." });
            onClose();
          } else {
            setFormError(message);
          }
        },
      },
    );
  };

  return (
    <BaseModal
      open
      title="Record refund"
      loading={mutation.isLoading}
      confirmLabel="Record refund"
      confirmDisabled={!trimmed || !confirmed}
      onClose={onClose}
      onConfirm={handleConfirm}
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <Box>
          <Typography fontSize="13px" color="text.secondary">
            {entrySourceLabel(entry)}
          </Typography>
          <Typography fontSize="20px" fontWeight={700} mt={0.5}>
            {amount}
          </Typography>
        </Box>
        <Typography fontSize="14px" lineHeight="20px">
          Transfer {amount} back to Livspace in one payment, then enter its UTR. A recorded
          refund can&apos;t be undone.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="UTR number"
          value={utr}
          onChange={(e) => setUtr(e.target.value)}
          error={!!fieldError}
          helperText={fieldError || " "}
        />
        <FormControlLabel
          control={<Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />}
          label={<Typography fontSize="14px">I have transferred {amount} in one payment</Typography>}
        />
        {formError && (
          <Typography role="alert" fontSize="13px" color="error">
            {formError}
          </Typography>
        )}
      </Box>
    </BaseModal>
  );
}
